import React, { useEffect, useState } from 'react';
import { CircleCheck, Clock, ListChecks, PartyPopper, Plus } from "lucide-react";
import LoadingSkeleton from "../../common/loading-skeleton/loading-skeleton";
import dashboard from "../../../resources/dashboard";
import { Poll, Result, Vote as IVote } from "../../../domains/models/poll";
import { Button } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { webSocketService } from "../../../services/WebSocketService";

interface IAdminPollVoteCard {
    navigateToPollsVote: () => void;
    data: Poll[] | IVote[];
    isLoading: boolean;
}

const AdminPollVoteCardView: React.FC<IAdminPollVoteCard> = (props) => {

    const resources = dashboard.adminPollVoteCard
    const isListEmpty = props.data.length > 0;
    const [liveData, setLiveData] = useState<Result[]>([]);

    useEffect(() => {
        if (!props.data) return;

        props.data.forEach((poll) => {
            webSocketService.subscribeToTopic(
                `poll-${poll.id}`,
                `/topic/polls/${poll.id}`,
                async (message) => {
                    try {
                        const result: Result =
                            typeof message === "string"
                                ? JSON.parse(message)
                                : message;


                        setLiveData((prev) => ([
                            ...prev,
                            result
                        ]))
                    } catch (e) {
                        console.log('Error while fetching the data')
                    }

                }
            );
        });

        return () => {
            props.data.forEach((poll) => {
                webSocketService.unsubscribeTopic(`poll-${poll.id}`);
            });
        };
    }, [props.data.length]);


    const getVisibilityStatus = (data: IVote | Poll) => data.anonymous ? 'Anonymous' : 'Non Anonymous';

    const getTimeDiffInHHmm = (start: Dayjs, end: Dayjs) => {
        const diffInMinutes = end.diff(start, "minute");

        if (diffInMinutes <= 0) {
            return "Time Expired";
        }

        const hours = Math.floor(diffInMinutes / 60);
        const minutes = diffInMinutes % 60;

        return `${hours}h ${minutes}m left`;
    };

    const getDeadline = (date: string | Date) => {
        //  UTC -> IST
        const targetDate = dayjs.utc(date).tz("Asia/Kolkata");

        // Current time in IST
        const today = dayjs().tz("Asia/Kolkata");

        if (today.isSame(targetDate, "day")) {
            return getTimeDiffInHHmm(today, targetDate);
        }

        const daysLeft = targetDate
            .startOf("day")
            .diff(today.startOf("day"), "day");

        return daysLeft > 0
            ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`
            : "Time Expired";
    };

    const getLeadingOptions = (data: IVote | Poll) => {
        let options = [...data.options];

        const liveRes = liveData.find((r) => r.pollId === data.id);

        if (liveRes) {
            options = options.map((opt) => {
                const liveResOpt = liveRes.options.find(
                    (r) => r.optionId === opt.id
                );

                return liveResOpt
                    ? {
                        ...opt,
                        voteCount: liveResOpt.voteCount,
                        votePercentage: liveResOpt.votePercentage,
                        voters: liveResOpt.voters,
                    }
                    : opt;
            });
        }

        const maxVoteCount = Math.max(...options.map(opt => opt.voteCount));
        if (maxVoteCount > 0) {
            return options.filter(opt => opt.voteCount === maxVoteCount);

        }
        return [];
    };


    return (
        <div className="dashboard-admin-poll-vote-card dashboard-card" onClick={props.navigateToPollsVote}>
            <div className={'dashboard-card__header'}>
                <span className={'dashboard-card__icon-wrapper dashboard-admin-poll-vote-card__icon-wrapper'}>
                       <ListChecks className={'dashboard-card__icon dashboard-admin-poll-vote-card__icon'}/>
                </span>

                <h5 className={'dashboard-card__title'}>
                    {resources.title}
                    <p className={'dashboard-card__description'}>
                        {'Manage polls and votes for your organization'}
                    </p>
                </h5>
                <span className={'dashboard-admin-poll-vote-card__count'}>
                    {props.data.length} Active
                </span>


            </div>
            <LoadingSkeleton loading={false}>
                {isListEmpty ?
                    (
                        <div className={'dashboard-admin-poll-vote-card__list'} onClick={(e) => e.stopPropagation()}>
                            {props.data.map((item: IVote | Poll) => {

                                const leadingOptions = getLeadingOptions(item);
                                const isVote = (data: IVote | Poll) => data.anonymous;

                                return (
                                    <div
                                        key={item.id}
                                        className={'dashboard-admin-poll-vote-card__list-item'}

                                    >
                                        <div className={'dashboard-admin-poll-vote-card__tag-wrapper'}>
                                        <span className={'dashboard-admin-poll-vote-card__tag result-status'}>
                                            {item.active ? 'Live' : 'Poll Ended'}
                                        </span>
                                            <span className={'dashboard-admin-poll-vote-card__tag visibility-status'}>
                                            {getVisibilityStatus(item)}
                                        </span>
                                            <span className={'dashboard-admin-poll-vote-card__tag deadline'}>
                                            <Clock className={'clock-icon'}/>
                                                {getDeadline(item.endTime)}
                                        </span>
                                        </div>
                                        <div className={'dashboard-admin-poll-vote-card__poll-details'}>
                                            <h5 className={'dashboard-admin-poll-vote-card__poll-title'}>
                                                {item.title}
                                            </h5>
                                            <div className={'dashboard-admin-poll-vote-card__poll-description'}
                                                 dangerouslySetInnerHTML={{__html: item.description}}
                                            />
                                            <div className={'dashboard-admin-poll-vote-card__poll-option'}>
                                                <div className={'dashboard-admin-poll-vote-card__poll-option-summary'}>
                                            <span className={'dashboard-admin-poll-vote-card__response-count'}>
                                                     <span className={'dashboard-admin-poll-vote-card__response-value'}>
                                                    {item.totalVotes}
                                                    </span>{' '}
                                                {item.totalVotes > 1 ? 'responses' : 'response'}
                                             </span>
                                                    {isVote(item) ? (
                                                        <div
                                                            className={'dashboard-admin-poll-vote-card__vote-status-message'}>
                                                            {resources.liveVotingDescription}
                                                        </div>
                                                    ) : (
                                                        leadingOptions.length === 1 ? (
                                                                <div
                                                                    className={'dashboard-admin-poll-vote-card__leading-detail'}>
                                                                    <div
                                                                        className={'dashboard-admin-poll-vote-card__leading-option'}>
                                                                        <CircleCheck
                                                                            className={'dashboard-admin-poll-vote-card__leading-icon'}/>
                                                                        <span
                                                                            className={'dashboard-admin-poll-vote-card__leading-label'}>
                                                             Leading - {leadingOptions[0].text}
                                                                </span>
                                                                        {leadingOptions[0].votePercentage &&
                                                                        <span
                                                                            className={'dashboard-admin-poll-vote-card__leading-value'}>
                                                                     {leadingOptions[0].votePercentage}%
                                                                </span>
                                                                        }
                                                                    </div>

                                                                    {leadingOptions[0].votePercentage &&
                                                                    <progress
                                                                        value={leadingOptions[0].votePercentage}
                                                                        max={"100"}
                                                                        className={'dashboard-admin-poll-vote-card__progress-bar'}/>
                                                                    }
                                                                    <span
                                                                        className={'dashboard-admin-poll-vote-card__option-note'}>
                                                                        {resources.moreLeadingOptionsLabel.replace('{optCount}',
                                                                            `${item.options.length - 1}`)}
                                                            </span>
                                                                </div>

                                                            ) :
                                                            leadingOptions.length > 1 ? (
                                                                <div
                                                                    className={'dashboard-admin-poll-vote-card__leading-tie'}>
                                                                    <div
                                                                        className={'dashboard-admin-poll-vote-card__leading-summary'}>
                                                                        <CircleCheck
                                                                            className={'dashboard-admin-poll-vote-card__leading-icon'}/>
                                                                        <span
                                                                            className={'dashboard-admin-poll-vote-card__leading-label'}>
                                                                            {resources.tiedLeadingOptionsLabel.replace('{optCount}',
                                                                                leadingOptions.length.toString())}
                                                                </span>
                                                                        <span
                                                                            className={'dashboard-admin-poll-vote-card__leading-value'}>
                                                                     {leadingOptions[0].votePercentage}%
                                                                </span>

                                                                    </div>
                                                                    <div
                                                                        className="dashboard-admin-poll-vote-card__tied-options">
                                                                        {leadingOptions.map((opt) => (
                                                                            <span
                                                                                className="dashboard-admin-poll-vote-card__tied-option">
                                                                            {opt.text}
                                                                        </span>
                                                                        ))}
                                                                    </div>
                                                                    <span
                                                                        className={'dashboard-admin-poll-vote-card__option-note'}>
                                                                        {resources.leadingOptionDescription}
                                                            </span>
                                                                </div>
                                                            ) : (
                                                                <div
                                                                    className={'dashboard-admin-poll-vote-card__vote-status-message'}>
                                                                    {resources.noResponsesDescription}
                                                                </div>
                                                            )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    )
                    : (
                        <div className={'dashboard-admin-poll-vote-card__empty-container'}>
                            <div className={'icon-wrapper'}>
                                <PartyPopper className={'poll-icon'}/>
                            </div>
                            <div className={'empty-container-content'}>
                                <h5 className={'title'}>{resources.emptyCardTitle}</h5>
                                <p className={'description'}>{resources.emptyCardContent}</p>
                            </div>
                            <Button
                                type={'link'}
                                icon={<Plus className={'link-icon'}/>}
                                className={'btn-link'}
                            >{resources.emptyCardButton}
                            </Button>
                        </div>
                    )}

            </LoadingSkeleton>
        </div>
    )


}

export default AdminPollVoteCardView;
